/**
 * 长辈模式工具方法
 * 支持三档放大：1.2 / 1.3 / 1.4
 * localStorage 存储放大系数值，'off' 表示关闭
 */

const ELDER_MODE_KEY = 'elderMode'; // localStorage 存储键
const BASE_FONT_SIZE_KEY = 'baseFontSize'; // html data 属性键

// 可选的放大档位
export const ELDER_SCALE_OPTIONS = [
  { label: '大', value: 1.2 },
  { label: '较大', value: 1.3 },
  { label: '超大', value: 1.4 }
];

// 暂时忽略长辈模式的页面，后续可以继续添加路由
export const ELDER_MODE_IGNORED_ROUTES = [
  '/shop/productList',
  '/shop/QuickProductList', //首页分类
  '/member/quickBuy', //快速购物
  '/store/businessManage/registerBrowse', //开卡浏览
  '/promote/equity', //荣誉殿堂
  '/member/addressList', //收货地址
  '/shop/home/productClassfiy' //商品分类
];

/**
 * 获取当前长辈模式状态
 * @returns {boolean} 是否开启
 */
export function getElderMode() {
  return getElderScale() > 1;
}

/**
 * 获取当前放大系数
 * @returns {number} 1.0 / 1.2 / 1.3 / 1.4
 */
export function getElderScale() {
  try {
    const val = parseFloat(localStorage.getItem(ELDER_MODE_KEY));
    if (val && val > 1) return val;
  } catch (e) {
    // 忽略 localStorage 读取异常
  }
  return 1.0;
}

/**
 * 获取当前 hash 路由路径
 * @returns {string} 不包含查询参数的路由路径
 */
export function getCurrentRoute() {
  const hash = window.location.hash || '';
  return hash.replace(/^#/, '').split('?')[0] || '/';
}

/**
 * 判断当前页面是否在长辈模式忽略列表中
 * @param {string} route 路由路径
 * @returns {boolean} 是否忽略
 */
export function isElderModeIgnoredRoute(route = getCurrentRoute()) {
  return ELDER_MODE_IGNORED_ROUTES.some(
    (ignoredRoute) => route === ignoredRoute || route.startsWith(`${ignoredRoute}/`)
  );
}

/**
 * 设置长辈模式放大系数并立即生效
 * @param {number|string} scale - 1.2 / 1.3 / 1.4，传 'off' 或 0 关闭
 */
export function setElderMode(scale) {
  try {
    if (!scale || scale === 'off' || scale <= 1) {
      localStorage.setItem(ELDER_MODE_KEY, 'off');
    } else {
      localStorage.setItem(ELDER_MODE_KEY, String(scale));
    }
  } catch (e) {
    // 忽略 localStorage 写入异常
  }
  applyElderMode();
}

/**
 * 应用长辈模式：始终基于未放大的根字体计算，避免重复放大
 * 被忽略的页面使用原始根字体，但不会清除用户的长辈模式设置
 */
export function applyElderMode() {
  const root = document.documentElement;
  const currentFontSize = parseFloat(root.style.fontSize) || 100;
  const savedBaseFontSize = parseFloat(root.dataset[BASE_FONT_SIZE_KEY]);
  const configuredScale = getElderScale();
  // PC/iPad 画布的逻辑根字号固定为 100px，不能使用 viewport 初始化阶段保存的 iPad 宽屏字号。
  const pcH5BaseFontSize = root.classList.contains('pc-h5-canvas') ? 100 : 0;
  const baseFontSize =
    pcH5BaseFontSize ||
    savedBaseFontSize ||
    currentFontSize / (configuredScale > 1 ? configuredScale : 1);
  const ignored = isElderModeIgnoredRoute();
  const activeScale = ignored ? 1 : configuredScale;
  const imageScale = activeScale > 1 ? 1 / activeScale : 1;

  root.style.fontSize = `${Math.floor(baseFontSize * activeScale)}px`;
  root.dataset[BASE_FONT_SIZE_KEY] = baseFontSize;
  root.style.setProperty('--elder-scale', activeScale);
  root.style.setProperty('--elder-ignore-scale', 1 / activeScale);
  root.style.setProperty('--elder-image-scale', imageScale);
  root.style.setProperty('--elder-ignore-percent', `${100 / activeScale}%`);
  root.classList.toggle('elder-mode', activeScale > 1);
  root.classList.toggle('elder-mode-page-ignored', ignored && configuredScale > 1);
}

/**
 * 初始化长辈模式，并监听 hash 路由切换
 * @returns {Function} 清理监听器的方法
 */
export function initElderMode() {
  const handleRouteChange = () => applyElderMode();
  // 供 PC/iPad 适配器在横竖屏或窗口尺寸变化后重新应用长辈模式，避免根字号被重置为未放大值。
  window.__applyElderMode = applyElderMode;
  window.addEventListener('hashchange', handleRouteChange, false);
  applyElderMode();

  return () => {
    window.removeEventListener('hashchange', handleRouteChange, false);
    // 只清理当前模块注册的钩子，避免覆盖后续可能接管该能力的新实现。
    if (window.__applyElderMode === applyElderMode) delete window.__applyElderMode;
  };
}

export default {
  getElderMode,
  setElderMode,
  getElderScale,
  getCurrentRoute,
  isElderModeIgnoredRoute,
  applyElderMode,
  initElderMode,
  ELDER_SCALE_OPTIONS,
  ELDER_MODE_IGNORED_ROUTES
};



// WEBPACK FOOTER //
// ./src/utils/elderMode.js